import { Fragment, useRef } from 'react';
import clsx from 'clsx';

import { css, dividerClasses, styled, useForkRef } from '@mui/material';
import { IcGripDotsVerticalRegular } from '@kep/kcds-icons';
import IcCheckRegular from '@kep/kcds-icons/IcCheckRegular';

import { useElementIsEllipsis } from '../../utils';
import { Checkbox } from '../Checkbox';
import { Divider } from '../Divider';
import { Radio } from '../Radio';
import { Tooltip } from '../Tooltip';
import { menuItemContentClasses } from './theme';

import type { ComponentProps, Ref } from 'react';
import type { TooltipProps } from '../Tooltip';
import type { MenuItemContentOwnerState, MenuItemContentProps } from './types';

export function MenuItemContent(props: MenuItemContentProps) {
  // 마이그레이션된 패턴: useThemeProps 제거, 기본값을 props 구조분해에서 처리
  const {
    ref,
    className,
    classes,
    color = 'default',
    startBadge,
    endBadge,
    disabled,
    disableTooltipOnEllipsis = false,
    ellipsisTooltipPlacement = 'right',
    dragHandleDisabled,
    dragHandleRef,
    icon,
    isDragging,
    onDragEnd,
    onDragStart,
    selected,
    selectedType = 'check',
    showDragHandle,
    subTexts,
    truncateSubTextIndex,
    suffix,
    text,
    ...others
  } = props;

  const ownerState: MenuItemContentOwnerState = {
    ...props,
    color,
    selectedType,
  };

  return (
    <MenuItemContentRoot
      ref={ref}
      className={clsx(className, classes?.root)}
      ownerState={ownerState}
      {...others}
    >
      {showDragHandle && (
        <MenuItemContentDragHandle
          ref={dragHandleRef}
          disabled={dragHandleDisabled}
          isDragging={isDragging}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          ownerState={ownerState}
        />
      )}
      {selectedType === 'checkbox' ? (
        <MenuItemContentCheckbox
          checked={selected}
          className={classes?.checkbox}
          ownerState={ownerState}
          tabIndex={-1}
        />
      ) : selectedType === 'radio' ? (
        <MenuItemContentRadio
          color={color}
          checked={selected}
          className={classes?.radio}
          ownerState={ownerState}
          tabIndex={-1}
        />
      ) : null}
      <MenuItemContentBody className={classes?.body} ownerState={ownerState}>
        <MenuItemContentMainTextFrame className={classes?.mainTextFrame} ownerState={ownerState}>
          {startBadge && (
            <MenuItemContentStartBadge className={classes?.startBadge} ownerState={ownerState}>
              {startBadge}
            </MenuItemContentStartBadge>
          )}
          {icon && (
            <MenuItemContentIcon className={classes?.icon} ownerState={ownerState}>
              {icon}
            </MenuItemContentIcon>
          )}
          <MenuItemContentText
            className={classes?.text}
            disableTooltipOnEllipsis={disableTooltipOnEllipsis}
            ellipsisTooltipPlacement={ellipsisTooltipPlacement}
            ownerState={ownerState}
          >
            {text}
          </MenuItemContentText>
          {endBadge && (
            <MenuItemContentEndBadge className={classes?.endBadge} ownerState={ownerState}>
              {endBadge}
            </MenuItemContentEndBadge>
          )}
          <MenuItemContentSpacer ownerState={ownerState} />
          {suffix && (
            <MenuItemContentSuffix className={classes?.suffix} ownerState={ownerState}>
              {suffix}
            </MenuItemContentSuffix>
          )}
          {selectedType === 'check' && selected && (
            <MenuItemContentCheckMark className={classes?.checkMark} ownerState={ownerState} />
          )}
        </MenuItemContentMainTextFrame>
        {subTexts && subTexts.length > 0 && (
          <MenuItemContentSubTextFrame className={classes?.subTextFrame} ownerState={ownerState}>
            {subTexts.map((subText, index) => {
              const truncateMode =
                truncateSubTextIndex !== undefined
                  ? truncateSubTextIndex === index
                    ? ('target' as const)
                    : ('preserve' as const)
                  : undefined;

              return (
                <Fragment key={index}>
                  {index !== 0 && <Divider orientation="vertical" />}
                  <MenuItemContentSubText
                    className={classes?.subText}
                    disableTooltipOnEllipsis={disableTooltipOnEllipsis || truncateMode !== 'target'}
                    ellipsisTooltipPlacement={ellipsisTooltipPlacement}
                    ownerState={ownerState}
                    truncateMode={truncateMode}
                  >
                    {subText}
                  </MenuItemContentSubText>
                </Fragment>
              );
            })}
          </MenuItemContentSubTextFrame>
        )}
      </MenuItemContentBody>
    </MenuItemContentRoot>
  );
}

MenuItemContent.displayName = 'MenuItemContent';

interface MenuItemContentEllipsisTextProps extends ComponentProps<'span'> {
  disableTooltipOnEllipsis?: boolean;
  ellipsisTooltipPlacement?: TooltipProps['placement'];
}

function MenuItemContentEllipsisText(props: MenuItemContentEllipsisTextProps) {
  const { disableTooltipOnEllipsis, ellipsisTooltipPlacement = 'right', ...others } = props;

  if (disableTooltipOnEllipsis) {
    return <span {...others} />;
  }

  return <EllipsisTooltipSpan ellipsisTooltipPlacement={ellipsisTooltipPlacement} {...others} />;
}

interface EllipsisTooltipSpanProps extends ComponentProps<'span'> {
  ellipsisTooltipPlacement: TooltipProps['placement'];
}

function EllipsisTooltipSpan(props: EllipsisTooltipSpanProps) {
  const { children, ellipsisTooltipPlacement, ref: forwardedRef, ...others } = props;
  const elementRef = useRef<HTMLSpanElement>(null);
  const { ref: ellipsisRef, isEllipsis } = useElementIsEllipsis<HTMLSpanElement>();
  const ref = useForkRef(elementRef, forwardedRef, ellipsisRef);

  return (
    <Tooltip
      message={isEllipsis ? elementRef.current?.innerText : undefined}
      placement={ellipsisTooltipPlacement}
    >
      <span ref={ref} {...others}>
        {children}
      </span>
    </Tooltip>
  );
}

// Styled Components - theme.ts에서 이전된 스타일
const MenuItemContentRoot = styled('div', {
  target: menuItemContentClasses.root,
})<{ ownerState: MenuItemContentOwnerState }>(
  ({ ownerState }) => css`
    display: flex;
    align-items: flex-start;
    padding: 6px 16px;
    overflow: hidden;
    ${ownerState.disabled &&
    css`
      opacity: 0.3;
      & > * {
        pointer-events: none;
      }
    `}
  `,
);

const MenuItemContentBody = styled('span', {
  target: menuItemContentClasses.body,
})<{ ownerState: MenuItemContentOwnerState }>(
  () => css`
    flex: 1 1 auto;
    min-width: 0px;
  `,
);

const MenuItemContentMainTextFrame = styled('div', {
  target: menuItemContentClasses.mainTextFrame,
})<{ ownerState: MenuItemContentOwnerState }>(
  () => css`
    display: flex;
    align-items: center;
    min-width: 0;
  `,
);

const MenuItemContentStartBadge = styled('div', {
  target: menuItemContentClasses.startBadge,
})<{ ownerState: MenuItemContentOwnerState }>(
  () => css`
    display: inline-flex;
    align-items: flex-start;
    margin-right: 4px;
    flex-shrink: 0;
  `,
);

const MenuItemContentEndBadge = styled('div', {
  target: menuItemContentClasses.endBadge,
})<{ ownerState: MenuItemContentOwnerState }>(
  () => css`
    display: inline-flex;
    align-items: flex-start;
    margin-left: 4px;
    flex-shrink: 0;
  `,
);

const MenuItemContentIcon = styled('div', {
  target: menuItemContentClasses.icon,
})<{ ownerState: MenuItemContentOwnerState }>(
  () => css`
    display: inline-flex;
    align-items: flex-start;
    flex-shrink: 0;
    margin-right: 4px;
    font-size: 20px;
  `,
);

const MenuItemContentText = styled(MenuItemContentEllipsisText, {
  target: menuItemContentClasses.text,
  shouldForwardProp: prop => prop !== 'ownerState',
})<{ ownerState: MenuItemContentOwnerState }>(({ theme, ownerState }) => {
  return css`
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    ${theme.typography['label/regular/md']}
    color: ${ownerState.color !== 'global' ? 'inherit' : theme.color['text/global/default']};
  `;
});

const MenuItemContentSubTextFrame = styled('div', {
  target: menuItemContentClasses.subTextFrame,
})<{ ownerState: MenuItemContentOwnerState }>(({ theme, ownerState }) => {
  return css`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    overflow: hidden;
    padding-block: 3px;
    ${theme.typography['body/normal/xs']}
    color: ${ownerState.color !== 'global'
      ? theme.color['text/weakest/default']
      : theme.color['text/global/weakest/default']};
    & .${dividerClasses.root} {
      height: 12px;
    }
  `;
});

const MenuItemContentSubText = styled(MenuItemContentEllipsisText, {
  target: menuItemContentClasses.subText,
  shouldForwardProp: prop => prop !== 'ownerState' && prop !== 'truncateMode',
})<{ ownerState: MenuItemContentOwnerState; truncateMode?: 'target' | 'preserve' }>(
  ({ truncateMode }) => css`
    ${truncateMode === 'target' &&
    css`
      flex: 0 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `}
    ${truncateMode === 'preserve' &&
    css`
      flex: 0 0 auto;
      white-space: nowrap;
    `}
  `,
);

const MenuItemContentSpacer = styled('span', {
  target: menuItemContentClasses.spacer,
})<{ ownerState: MenuItemContentOwnerState }>(
  () => css`
    flex: 1 1 auto;
    min-width: 0px;
  `,
);

const MenuItemContentSuffix = styled('span', {
  target: menuItemContentClasses.suffix,
})<{ ownerState: MenuItemContentOwnerState }>(({ theme, ownerState }) => {
  return css`
    margin-left: 4px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    ${theme.typography['label/regular/xs']}
    color: ${ownerState.color !== 'global'
      ? theme.color['text/weaker/default']
      : theme.color['text/global/weakest/default']};
  `;
});

const MenuItemContentCheckbox = styled(Checkbox, {
  target: menuItemContentClasses.checkbox,
})<{ ownerState: MenuItemContentOwnerState }>(
  () => css`
    margin-top: 2px;
    margin-right: 8px;
    font-size: 16px;
  `,
);

const MenuItemContentRadio = styled(Radio, {
  target: menuItemContentClasses.radio,
})<{ ownerState: MenuItemContentOwnerState }>(
  () => css`
    margin-top: 2px;
    margin-right: 8px;
    font-size: 16px;
  `,
);

const MenuItemContentCheckMark = styled(IcCheckRegular, {
  target: menuItemContentClasses.checkMark,
})<{ ownerState: MenuItemContentOwnerState }>(
  () => css`
    margin-left: 4px;
    font-size: 16px;
  `,
);

interface DragHandleProps {
  ownerState: MenuItemContentOwnerState;
  disabled?: boolean;
  isDragging?: boolean;
  isOverDropZone?: boolean;
  onDragEnd?: () => void;
  onDragStart?: () => void;
  ref?: Ref<HTMLDivElement>;
}

const MenuItemContentDragHandle = ({
  ref,
  disabled,
  onDragStart,
  onDragEnd,
  ownerState,
}: DragHandleProps) => {
  const handleMouseDown = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering parent click events
    if (!disabled && onDragStart) {
      onDragStart();
    }
  };

  const handleMouseUp = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering parent click events
    if (onDragEnd) {
      onDragEnd();
    }
  };

  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent triggering parent click events
  };

  return (
    <MenuItemContentDragHandleRoot
      ref={ref}
      disabled={disabled}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onClick={handleClick}
      ownerState={ownerState}
    >
      <IcGripDotsVerticalRegular />
    </MenuItemContentDragHandleRoot>
  );
};

const MenuItemContentDragHandleRoot = styled('div')<{
  ownerState: MenuItemContentOwnerState;
  disabled?: boolean;
}>`
  align-self: flex-start;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  margin-top: 4px;
  margin-left: -14px;
  margin-right: 2px;
  font-size: 12px;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  cursor: ${({ disabled }) => {
    if (disabled) return 'initial';
    return 'grab';
  }};
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};

  color: ${({ theme, disabled }) =>
    disabled ? theme.color['icon/disabled'] : theme.color['icon/weakest/default']};

  &:active {
    cursor: ${({ disabled }) => {
      if (disabled) return 'initial';
      return 'grabbing';
    }};
  }
`;
